import { computed, Ref, ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuthStore } from "../pinia/auth";
import { getAclStatus } from "../shared/services/aclStatus";

export const REFERRER_QUERY_KEY: string = "referrer";

interface ACL {
  URL: string;
  Status: number;
  AutoBrowseUsers?: boolean;
}

interface UseRedirectUrlReturn {
  redirectUrl: Ref<string | null>;
  redirectSeconds: Ref<number>;
  shouldShowRedirect: Ref<boolean>;
  cancelRedirect: () => Promise<void>;
  getReferrerUrl: () => string | null;
}

/**
 * Composable for handling redirect URL logic (referrer and autobrowse)
 * Manages URL validation, priority, and redirection logic
 *
 * Priority: Referrer URL (if matches granted ACL) > Autobrowse URL
 *
 * Autobrowse logic:
 * - Automatically extracts autobrowse URL from ACLs where AutoBrowseUsers=true
 * - Only triggers if exactly one ACL has AutoBrowseUsers=true
 * - Respects user's dismissal preference (authStore.autoBrowseDismissed)
 *
 * @param acls - Array of ACL objects with URL, Status, and AutoBrowseUsers properties
 * @returns Object with redirect utilities
 */
export function useRedirectUrl(acls: Ref<Array<ACL>> | Array<ACL>): UseRedirectUrlReturn {
  const router = useRouter();
  const route = useRoute();
  const authStore = useAuthStore();

  // Internal state - get referrer from query params
  const referrerFromQuery = (route.query[REFERRER_QUERY_KEY] as string) || null;
  const referrerUrl = ref<string | null>(referrerFromQuery);

  // Handle both Ref and direct values for acls
  const aclsValue = computed(() => (typeof acls === "object" && "value" in acls ? acls.value : acls));

  // Determine if autobrowse is enabled (user hasn't dismissed it)
  const autoBrowseEnabled = computed(() => !authStore.autoBrowseDismissed);

  /**
   * Extract autobrowse URL from ACLs
   * Only returns a URL if exactly one ACL has AutoBrowseUsers=true
   */
  const autoBrowseUrl = computed(() => {
    if (!autoBrowseEnabled.value) {
      return null;
    }

    // Only autobrowse if there is only 1 acl
    if (aclsValue.value.length != 1) {
      return null;
    }

    const autoBrowseAcls = aclsValue.value.filter((acl) => acl.AutoBrowseUsers === true);

    // Only proceed if exactly one ACL has AutoBrowseUsers enabled
    if (autoBrowseAcls.length !== 1) {
      return null;
    }

    const acl = autoBrowseAcls[0];

    // dont autobrowse if the stauts is not granted
    const status = getAclStatus(acl.Status);
    if (!status?.grantIssued) {
      return null;
    }

    // Validate the URL
    if (isValidHttpUrl(acl.URL)) {
      return acl.URL;
    }

    return null;
  });

  /**
   * Validates if a URL is valid and uses http/https protocol
   */
  const isValidHttpUrl = (url: string | null | undefined): boolean => {
    if (!url) {
      return false;
    }

    try {
      const urlObj = new URL(url);
      return urlObj.protocol === "http:" || urlObj.protocol === "https:";
    } catch {
      return false;
    }
  };

  /**
   * Checks if the referrer URL matches the host of the provided URL
   * Compares protocol, hostname, and port
   */
  const matchesUrlHost = (referrer: string, urlToCompare: string): boolean => {
    try {
      const referrerObj = new URL(referrer);
      const compareUrl = new URL(urlToCompare);

      return (
        referrerObj.protocol === compareUrl.protocol &&
        referrerObj.hostname === compareUrl.hostname &&
        referrerObj.port === compareUrl.port
      );
    } catch {
      return false;
    }
  };

  /**
   * Determines if referrer matches any granted ACL
   */
  const referrerMatchesGrantedAcl = computed(() => {
    if (!isValidHttpUrl(referrerUrl.value)) {
      return false;
    }

    for (let acl of aclsValue.value) {
      const status = getAclStatus(acl.Status);
      if (status?.grantIssued) {
        try {
          if (matchesUrlHost(referrerUrl.value!, acl.URL)) {
            return true;
          }
        } catch {}
      }
    }

    return false;
  });

  /**
   * Determines the URL to redirect to
   * Priority: Referrer URL (if matches granted ACL) > Autobrowse URL
   */
  const redirectUrl = computed(() => {
    // Check referrer first (highest priority)
    if (referrerMatchesGrantedAcl.value && referrerUrl.value) {
      return referrerUrl.value;
    }

    // Fall back to autobrowse if enabled and available
    if (autoBrowseUrl.value) {
      return autoBrowseUrl.value;
    }

    return null;
  });

  /**
   * Determines if the redirect dialog should be shown
   */
  const shouldShowRedirect = computed(() => {
    return redirectUrl.value !== null;
  });

  /**
   * Determines the countdown seconds based on redirect type
   * - Referrer: 5 seconds
   * - Autobrowse: 3 seconds
   */
  const redirectSeconds = computed(() => {
    if (referrerMatchesGrantedAcl.value && referrerUrl.value) {
      return 5; // Referrer redirect
    }
    return 3; // Autobrowse redirect
  });

  /**
   * Cancels the redirect and clears referrer from URL query params
   * If cancelling an autobrowse redirect, also dismisses autobrowse in store
   */
  const cancelRedirect = async (): Promise<void> => {
    // If we're canceling an autobrowse redirect, dismiss it in the store
    if (redirectUrl.value === autoBrowseUrl.value && autoBrowseUrl.value) {
      authStore.dismissAutoBrowse();
    }

    // Clear internal referrer state
    referrerUrl.value = null;

    // Remove referrer from URL query params if it exists
    if (route.query.referrer) {
      const newQuery = { ...route.query };
      delete newQuery[REFERRER_QUERY_KEY];
      await router.replace({ query: newQuery });
    }
  };

  /**
   * Gets and returns the referrer URL from the query if it is valid
   * Returns null if the referrer is not a valid HTTP/HTTPS URL
   */
  const getReferrerUrl = (): string | null => {
    if (isValidHttpUrl(referrerUrl.value)) {
      return referrerUrl.value;
    }
    return null;
  };

  return {
    redirectUrl,
    redirectSeconds,
    shouldShowRedirect,
    cancelRedirect,
    getReferrerUrl,
  };
}
